<script>
import ExhibitorRow from "./exhibitor-row.vue"
import ExhibitorChannelPosts from "./exhibitor-channel-posts.vue"
import FavoriteMixin from "./favorite-mixin.js"
import StaffThumb from "./staff-thumb.vue"
import ImageViewer from "../channel/image-viewer.vue"

export default {
  mixins: [FavoriteMixin],
  components: {ExhibitorRow, ExhibitorChannelPosts, StaffThumb, ImageViewer},
  props: {
    exhibitorId: {type: String, required: true},
    exhibitorType: {type: String, default: 'exhibitor'},
  },

  data() {
    return {
      exhibitor: {},
      galleryVisible: false,
      galleryStartIndex: 0,
      channelSubscribed: false,
      isCompanyMember: false,
    }
  },

  computed: {
    galleryImages() {
      return (this.exhibitor.profile_images || []).map(img => ({
        src: img.image,
        name: img.title || ''
      }))
    }
  },

  watch: {
    exhibitorId: {
      handler() {
        this.fetchExhibitor()
      },
    },
  },

  created() {
    this.fetchExhibitor()
  },

  methods: {
    fetchExhibitor: function () {
      let me = this
      let params = this.$route.query.lead_id ? {lead_id: this.$route.query.lead_id} : {}
      this.$api.get(`exhibitors/${this.exhibitorId}/`, {params: params}).then(function(response) {
        me.exhibitor = response.data
        me.fetchSubscribeState()
      }).catch(function(error){console.error(error)})
    },

    fetchSubscribeState() {
      if (!this.exhibitor.sfid) return
      this.$chat.get('exhibitor-channel/subscribe/', {params: {exhibitor_sfid: this.exhibitor.sfid}})
        .then(({data}) => {
          this.channelSubscribed = data.subscribed
          this.isCompanyMember = !!data.is_company_member
        })
        .catch(() => {})
    },

    toggleSubscribe() {
      // Crew members are permanent members of their own channel (backend
      // refuses unsubscribing anyway).
      if (this.isCompanyMember) return
      const payload = {exhibitor_sfid: this.exhibitor.sfid}
      const req = this.channelSubscribed
        ? this.$chat.delete('exhibitor-channel/subscribe/', {data: payload})
        : this.$chat.post('exhibitor-channel/subscribe/', payload)
      req.then(({data}) => { this.channelSubscribed = data.subscribed }).catch(() => {})
    },

    onFavoritesChanged(sfId) {
      this.getFavorites()
      let key = `${this.exhibitorType}s`
      let favExhibitors = localStorage.getItem(key)
      favExhibitors = favExhibitors ? JSON.parse(favExhibitors) : {}
      if (favExhibitors[sfId]) {
        delete favExhibitors[sfId]
      } else {
        let exhibitor = this.exhibitors.find(exhibitor => exhibitor.sfid === sfId)
        favExhibitors[sfId] = exhibitor
      }
      localStorage.setItem(key, JSON.stringify(favExhibitors))
    },

    openGallery(idx) {
      this.galleryStartIndex = idx
      this.galleryVisible = true
    },

    truncateDescription(description) {
      if (!description) return ''
      const stripped = description.replace(/<[^>]*>/g, '')
      const words = stripped.split(' ')
      if (words.length <= 10) return stripped
      return words.slice(0, 10).join(' ') + '...'
    },

    navigateToListWithCategory(cat) {
      const shortName = this.$route && this.$route.params.shortName
      const route = shortName
        ? {name: 'Event Exhibitors', params: {shortName}, query: {categories__id: cat.id}}
        : {name: 'Exhibitors', query: {categories__id: cat.id}}

      const spaRouteNames = ['Exhibitor Detail', 'Event Exhibitor Detail']
      const inSpa = this.$route && spaRouteNames.includes(this.$route.name)

      if (inSpa) {
        this.$router.push(route).catch(() => {})
      } else {
        this.$router.push(route).then(() => window.location.reload()).catch(() => window.location.reload())
      }
    },

    messageExhibitor() {
      // Open (or reuse) the visitor's inquiry channel to this exhibitor's company
      // and jump into the chat. The backend resolves the company from the sfid.
      this.$chat.post('exhibitor/request/', {exhibitor_sfid: this.exhibitor.sfid})
        .then(({data}) => this.$router.push(`/channel-${data.name}`))
        .catch(error => console.error(error))
    },

    getProductUrl(product) {
      if (!this.exhibitor.id || !this.exhibitor.company_name || !product.slug) return '#'
      const slugified = this.exhibitor.company_name.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      return `/exhibitors/${this.exhibitor.id}-${slugified}/${product.slug}`
    },
  },


}
</script>

<template>
  <span v-if="exhibitor.company_name">
    <exhibitor-row :exhibitor="exhibitor" :listDisplay="false"
      @favorite-changed="onFavoritesChanged($event)"
      @category-clicked="navigateToListWithCategory" >

      <div v-html="exhibitor.lead.html" class="py-3"/>

    </exhibitor-row>

    <div class="mt-2 d-flex flex-wrap gap-2">
      <button class="btn btn-primary btn-sm" @click.prevent="messageExhibitor">
        <i class="bi bi-chat-dots"></i> Nachricht an Aussteller
      </button>
      <button v-if="isCompanyMember" class="btn btn-sm btn-success" disabled>
        <i class="bi bi-megaphone"></i> Dein Kanal
      </button>
      <button v-else class="btn btn-sm" :class="channelSubscribed ? 'btn-success' : 'btn-outline-secondary'"
       @click.prevent="toggleSubscribe">
        <i class="bi" :class="channelSubscribed ? 'bi-bell-fill' : 'bi-bell'"></i>
        {{ channelSubscribed ? 'Abonniert' : 'Kanal abonnieren' }}
      </button>
    </div>

    <!-- Public channel posts (read-only, no subscription required) -->
    <exhibitor-channel-posts v-if="exhibitor.sfid" :exhibitor-sfid="exhibitor.sfid" />

    <!-- Image Gallery Section -->
    <div v-if="exhibitor.profile_images && exhibitor.profile_images.length > 0" class="row mt-4">
      <div class="col-12">
        <hr>
        <h5 class="mb-3"><i class="bi bi-images"></i> Bildergalerie</h5>
        <div class="row g-3">
          <div v-for="(img, idx) in exhibitor.profile_images" :key="img.id"
               class="col-6 col-md-4 col-lg-3">
            <div class="gallery-thumb" @click="openGallery(idx)">
              <div class="ratio ratio-1x1">
                <img :src="img.image" :alt="img.title || exhibitor.company_name"
                     class="object-fit-cover rounded">
              </div>
              <p v-if="img.title" class="small text-muted mt-1 mb-0">{{ img.title }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Booth Staff Section -->
    <div v-if="exhibitor.booth_staff && exhibitor.booth_staff.length > 0" class="mt-4">
      <hr>
      <h5 class="mb-3"><i class="bi bi-person-badge"></i> Standpersonal</h5>
      <div class="d-flex my-3" style="flex-direction: row; gap: 16px; flex-wrap: wrap;">
        <staff-thumb v-for="(staff, idx) in exhibitor.booth_staff" :key="idx" :staff="staff" />
      </div>
    </div>

    <!-- Products Section -->
    <div v-if="exhibitor.products && exhibitor.products.length > 0" class="row mt-4">
      <div class="col-12">
        <hr>
        <h5 class="mb-3"><i class="bi bi-box-seam"></i> Unsere Produkte</h5>
        <div class="row g-3">
          <div v-for="product in exhibitor.products" :key="product.uuid" class="col-12 col-sm-6 col-md-4 col-lg-3">
            <router-link :to="getProductUrl(product)" class="text-decoration-none">
              <div class="product-card border rounded p-2">
                <div v-if="product.images && product.images.length > 0" class="ratio ratio-1x1 mb-2">
                  <img :src="product.images[0].image"
                       class="object-fit-cover rounded"
                       :alt="product.name">
                </div>
                <h6 class="mb-1 text-dark">{{ product.name }}</h6>
                <p v-if="product.description" class="small text-muted mb-0" v-html="truncateDescription(product.description)"></p>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Attachments Section -->
    <div v-if="exhibitor.profile_attachments && exhibitor.profile_attachments.length > 0" class="row mt-4">
      <div class="col-12">
        <hr>
        <h5 class="mb-3"><i class="bi bi-file-earmark-pdf"></i> Downloads</h5>
        <div class="list-group">
          <a
            v-for="attachment in exhibitor.profile_attachments"
            :key="attachment.uuid"
            :href="attachment.file"
            target="_blank"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
            <div>
              <i class="bi bi-file-pdf text-danger me-2"></i>
              <span>{{ attachment.name || 'Download' }}</span>
            </div>
            <i class="bi bi-download"></i>
          </a>
        </div>
      </div>
    </div>

    <!-- Modal Gallery -->
    <image-viewer
      v-if="galleryVisible"
      :images="galleryImages"
      :start-index="galleryStartIndex"
      @close="galleryVisible = false"
    />

  </span>
  <div v-else>
    <h2 class="text-center">not found</h2>
  </div>
</template>

<style scoped>
.category-badge {
  cursor: pointer;
}
.category-badge:hover {
  opacity: 0.8;
}

.gallery-thumb {
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
}

.gallery-thumb img {
  transition: transform 0.2s;
}

.gallery-thumb:hover img {
  transform: scale(1.05);
}

.product-card {
  transition: box-shadow 0.2s, transform 0.2s;
  height: 100%;
  cursor: pointer;
}

.product-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.product-card h6 {
  font-size: 0.95rem;
  font-weight: 600;
}

.product-card p {
  font-size: 0.85rem;
  line-height: 1.3;
}
</style>
